import {
    AppointmentSlotData,
    BookingResponse,
} from '@viewbid/ts-angular-client';
import { PickupSlot } from '../model/pickup-slot';
import moment from 'moment';

export class PickupSlotService {
    /** FeatureFlag.PickupFulfillment — maps a scheduler's available slots
     *  (GET /schedulers/:schedulersId/available-slots) onto the same
     *  PickupSlot shape the calendar/time-slot components already render.
     *  Unlike the legacy filterPickupSlotsByDate, `remaining` already
     *  accounts for existing bookings server-side, so no local subtraction
     *  is needed. */
    static filterAppointmentSlotsByDate(
        slots: AppointmentSlotData[],
        selectedDate: Date,
    ): PickupSlot[] {
        const targetDate: string = this.formatDateToYMD(selectedDate);
        return slots
            .filter(
                (slot: AppointmentSlotData) =>
                    moment(slot.start).format('YYYY-MM-DD') === targetDate,
            )
            .map((slot: AppointmentSlotData) => {
                const timeStr: string = moment(slot.start).format('HH:mm');
                const [hour, minute]: number[] = timeStr.split(':').map(Number);
                const hour12: number =
                    hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                return {
                    slotLabel: `${hour12}:${String(minute).padStart(2, '0')}`,
                    slotValue: timeStr,
                    availableSlots: slot.remaining,
                    zone: hour < 12 ? 'AM' : 'PM',
                };
            });
    }

    static filterPickupSlotsByDate(
        bookingData: BookingResponse[],
        selectedDate: Date,
    ): PickupSlot[] {
        // Initialize variables from bookingData
        const targetDate: string = this.formatDateToYMD(selectedDate);
        const bookingsForDate: BookingResponse[] = bookingData.filter(
            (booking: BookingResponse) => {
                return this.legacySlotClockTime(booking).startsWith(
                    `${targetDate} `,
                );
            },
        );

        const maxSlots: number = 4;

        const filteredSlots: PickupSlot[] = [
            ...new Set(
                bookingsForDate.map((booking: BookingResponse) =>
                    this.legacySlotClockTime(booking).slice(11, 16),
                ),
            ),
        ].map((slotValue: string) => {
            const [slotHour] = slotValue.split(':').map(Number);
            return {
                slotLabel: `${slotHour > 12 ? slotHour - 12 : slotHour}:${slotValue.split(':')[1]}`,
                slotValue: slotValue,
                availableSlots: maxSlots,
                zone: slotHour < 12 ? 'AM' : 'PM',
            };
        });

        // Get current date and time
        const currentDate: Date = new Date();
        const currentDateString: string = this.formatDateToYMD(currentDate);
        const currentTime: string = `${currentDate.getHours() < 10 ? '0' : ''}${currentDate.getHours()}:${currentDate.getMinutes() < 10 ? '0' : ''}${currentDate.getMinutes()}`;

        // Set availableSlots to 0 for past times if the target date is today
        if (targetDate === currentDateString) {
            filteredSlots.forEach((slot: PickupSlot) => {
                if (slot.slotValue < currentTime) {
                    slot.availableSlots = 0;
                }
            });
        }

        // Update availableSlots based on bookings
        bookingsForDate.forEach((booking: BookingResponse) => {
            const bookingTime: string = this.legacySlotClockTime(booking).slice(
                11,
                16,
            );
            const slot: PickupSlot | undefined = filteredSlots.find(
                (slot: PickupSlot) => slot.slotValue === bookingTime,
            );

            if (slot && booking.bookings > 0) {
                slot.availableSlots = Math.max(
                    slot.availableSlots - booking.bookings,
                    0,
                );
            }
        });

        return filteredSlots;
    }

    static formatDateToYMD(date: Date): string {
        const year: number = date.getFullYear();
        const month: string = String(date.getMonth() + 1).padStart(2, '0');
        const day: string = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private static legacySlotClockTime(booking: BookingResponse): string {
        return moment
            .utc(Number(booking.pickupTime))
            .format('YYYY-MM-DD HH:mm');
    }
}
